import React from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import { IEcommerceQuestionViewProps } from '../../types'
import style from './Header.module.scss'
import commonStyle from '../Costco.module.scss'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

interface IHeaderProps {
  schema: IEcommerceQuestionViewProps['schema']
  total: number
  findability: boolean
  clearSelection: () => void
  onHomeClick: () => void
  onCartOpen: () => void
}

const Header = ({ schema, total, findability, clearSelection, onHomeClick, onCartOpen }: IHeaderProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  return (
    <>
      {viewport.mobile ? (
        <header className={style.headerMobile} data-testid="SW-EC-Header">
          <button className={style.arrowBack} onClick={onHomeClick}>
            <i className="pi pi-chevron-left" />
          </button>
          <div className={style.input}>
            <EmptyLink color="#0060A9">
              <i className="pi pi-search" />
            </EmptyLink>
            <div
              className={style.search}
              dangerouslySetInnerHTML={{ __html: schema.search_condition || t('questions', 'ec', 'costco', 'search') }}
              onClick={clearSelection}
            />
            <EmptyLink color="#0060A9">
              <i className="pi pi-times" />
            </EmptyLink>
          </div>
        </header>
      ) : (
        <header className={style.header} data-testid="SW-EC-Header">
          <div className={style.headerLinks}>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>{t('questions', 'ec', 'costco', 'costcoNext')}</div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>{t('questions', 'ec', 'costco', 'suppiesLast')}</div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>{t('questions', 'ec', 'costco', 'onlineOnly')}</div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>{t('questions', 'ec', 'costco', 'treasure')}</div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>{t('questions', 'ec', 'costco', 'whatNew')}</div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>{t('questions', 'ec', 'costco', 'memberFavorites')}</div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>{t('questions', 'ec', 'costco', 'emailOffers')}</div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>{t('questions', 'ec', 'costco', 'treasure')}</div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.headerLinkItem}>
                {t('questions', 'ec', 'costco', 'us')}&nbsp;
                <i className={`${commonStyle.costcoIcon} ${commonStyle.arrowDown}`} />
              </div>
            </EmptyLink>
          </div>
          <nav className={style.nav}>
            <div className={style.logo} onClick={onHomeClick} />
            <div className={style.search}>
              <div
                className={style.condition}
                dangerouslySetInnerHTML={{
                  __html: schema.search_condition || t('questions', 'ec', 'costco', 'search')
                }}
                onClick={clearSelection}
              />
              <EmptyLink color="#0060A9">
                <i className={`${commonStyle.costcoIcon} ${commonStyle.inputSearch} ${style.inputSearchIcon}`} />
              </EmptyLink>
            </div>
            <div className={style.navLinks}>
              <EmptyLink color="#0060A9">
                <div className={style.navLinkItem}>{t('questions', 'ec', 'costco', 'sign')}</div>
              </EmptyLink>
              <div className={style.lineVertical} />
              <EmptyLink color="#0060A9">
                <div className={style.navLinkItem}>{t('questions', 'ec', 'costco', 'order')}</div>
              </EmptyLink>
              <div className={style.lineVertical} />

              {findability ? (
                <EmptyLink color="#0060A9">
                  <div className={style.cart}>
                    <i className={`${commonStyle.costcoIcon} ${commonStyle.cart} ${style.cartIcon}`} />
                    {total > 0 && <div className={style.totals}>{total > 99 ? '99+' : total}</div>}
                    <div className={style.cartText}>{t('questions', 'ec', 'costco', 'cart')}</div>
                  </div>
                </EmptyLink>
              ) : (
                <button className={style.cart} onClick={onCartOpen}>
                  <i className={`${commonStyle.costcoIcon} ${commonStyle.cart} ${style.cartIcon}`} />
                  {total > 0 && <div className={style.totals}>{total > 99 ? '99+' : total}</div>}
                  <div className={style.cartText}>{t('questions', 'ec', 'costco', 'cart')}</div>
                </button>
              )}
            </div>
          </nav>
          <div className={style.filters}>
            <div className={style.filtersInner}>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <i className={`${commonStyle.costcoIcon} ${commonStyle.menu} ${style.menuIcon}`} />
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'shop')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'grocery')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'sameDay2')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'deals')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'businessDelivery')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'optical')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'pharmacy')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'services')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'photo')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'travel')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'membership')}</span>
                </div>
              </EmptyLink>
              <EmptyLink color="#0060A9">
                <div className={style.filterItem}>
                  <span className={style.filterText}>{t('questions', 'ec', 'costco', 'locations')}</span>
                </div>
              </EmptyLink>
            </div>
          </div>
          <div className={style.addressLine}>
            <div className={style.addressLineInner}>
              <div className={style.addressSettings}>
                <div className={style.settingItem}>
                  <div className={style.itemTitle}>{t('questions', 'ec', 'costco', 'myWarehouse')}</div>
                  <EmptyLink color="#0060A9">
                    <div className={style.itemContent}>
                      <div className={style.locIcon} />
                      {t('questions', 'ec', 'costco', 'setMyWarehouse')}
                    </div>
                  </EmptyLink>
                </div>
                <div className={style.settingItem}>
                  <div className={style.itemTitle}>{t('questions', 'ec', 'costco', 'deliveryLoc')}</div>
                  <EmptyLink color="#0060A9">
                    <div className={style.itemContent}>
                      <div className={style.boxIcon} />
                      {t('questions', 'ec', 'costco', 'myIndex')}
                    </div>
                  </EmptyLink>
                </div>
              </div>
              <div className={style.options}>
                <EmptyLink color="#0060A9">
                  <div className={style.option}>{t('questions', 'ec', 'costco', 'lists')}</div>
                </EmptyLink>
                &nbsp;|&nbsp;
                <EmptyLink color="#0060A9">
                  <div className={style.option}>{t('questions', 'ec', 'costco', 'buyAgain')}</div>
                </EmptyLink>
              </div>
            </div>
          </div>
        </header>
      )}
    </>
  )
}
export default Header
